import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { FiUpload, FiTrash2, FiPlus, FiImage, FiVideo, FiExternalLink, FiPlay, FiX } from 'react-icons/fi';

// Helper function to get API URL - uses relative path in production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('justasmile-events.com') || hostname.includes('app.justasmile')) {
      return '';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const API_URL = getApiUrl();

export default function ContentManagerPage() {
  const [logos, setLogos] = useState([]);
  const [models, setModels] = useState([]);
  const [realizations, setRealizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  
  const [newRealization, setNewRealization] = useState({ youtubeUrl: '', title: '', category: 'videobooth-360' });
  const [newModel, setNewModel] = useState({ name: '', format: 'paysage' });
  
  const logoInputRef = useRef(null);
  const modelInputRef = useRef(null);

  useEffect(() => {
    loadAllContent();
  }, []);

  const loadAllContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Load logos from site settings
      const siteRes = await fetch(`${API_URL}/api/my-site`, { headers });
      if (siteRes.ok) {
        const siteData = await siteRes.json();
        const rawLogos = siteData.site_settings?.client_logos || [];
        // Normalize logos to object format { url, name, link }
        const normalizedLogos = rawLogos.map(logo => {
          if (typeof logo === 'string') {
            return { url: logo, name: '', link: '' };
          }
          if (logo?.url) {
            const fullUrl = logo.url.startsWith('http') ? logo.url : `${API_URL}${logo.url}`;
            return { url: fullUrl, name: logo.name || '', link: logo.link || '' };
          }
          return null;
        }).filter(Boolean);
        setLogos(normalizedLogos);
      }
      
      // Load models
      const modelsRes = await fetch(`${API_URL}/api/photo-models`, { headers });
      if (modelsRes.ok) {
        const modelsData = await modelsRes.json();
        setModels(modelsData.models || []);
      }
      
      // Load realizations
      const realRes = await fetch(`${API_URL}/api/realizations`, { headers });
      if (realRes.ok) {
        const realData = await realRes.json();
        setRealizations(realData.realizations || []);
      }
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Upload image helper
  const uploadImage = async (file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/api/media/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    
    if (!response.ok) throw new Error('Upload failed');
    const data = await response.json();
    return `${API_URL}${data.url}`;
  };

  // ========== LOGOS ==========
  const handleLogoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Filter valid image files
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      toast.error('Aucun fichier image valide');
      return;
    }
    
    setUploading(true);
    setUploadProgress({ current: 0, total: imageFiles.length });
    
    try {
      // Upload all files in PARALLEL for speed (max 5 concurrent)
      const uploadedLogos = [];
      const failedFiles = [];
      
      // Process in batches of 5 for better performance
      const batchSize = 5;
      for (let i = 0; i < imageFiles.length; i += batchSize) {
        const batch = imageFiles.slice(i, i + batchSize);
        const batchPromises = batch.map(async (file) => {
          try {
            const url = await uploadImage(file);
            return { url, name: '', link: '', success: true };
          } catch (err) {
            console.error(`Failed to upload ${file.name}:`, err);
            return { success: false, name: file.name };
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        
        batchResults.forEach(result => {
          if (result.success) {
            uploadedLogos.push({ url: result.url, name: result.name || '', link: result.link || '' });
          } else {
            failedFiles.push(result.name);
          }
        });
        
        setUploadProgress({ current: Math.min(i + batchSize, imageFiles.length), total: imageFiles.length });
      }
      
      if (uploadedLogos.length === 0) {
        toast.error('Échec de l\'upload de tous les fichiers');
        return;
      }
      
      // Save to site content (site_settings.client_logos)
      const token = localStorage.getItem('token');
      const newLogos = [...logos, ...uploadedLogos];
      
      // Convert for backend (remove API_URL prefix)
      const logosForBackend = newLogos.map(logo => ({ 
        name: logo.name || '', 
        url: (logo.url || '').replace(API_URL, ''), 
        link: logo.link || '' 
      }));
      
      await fetch(`${API_URL}/api/my-site`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ site_settings: { client_logos: logosForBackend } })
      });
      
      setLogos(newLogos);
      
      if (failedFiles.length > 0) {
        toast.warning(`${uploadedLogos.length} logo(s) ajouté(s), ${failedFiles.length} échec(s)`);
      } else {
        toast.success(`${uploadedLogos.length} logo(s) ajouté(s)`);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const updateLogoLink = async (index, newLink) => {
    const token = localStorage.getItem('token');
    const newLogos = [...logos];
    newLogos[index] = { ...newLogos[index], link: newLink };
    setLogos(newLogos);
    
    // Debounce the API call - save after user stops typing
    clearTimeout(window.logoLinkSaveTimeout);
    window.logoLinkSaveTimeout = setTimeout(async () => {
      try {
        const logosForBackend = newLogos.map(logo => ({ 
          name: logo.name || '', 
          url: (logo.url || '').replace(API_URL, ''), 
          link: logo.link || '' 
        }));
        
        await fetch(`${API_URL}/api/my-site`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ site_settings: { client_logos: logosForBackend } })
        });
        
        toast.success('Lien sauvegardé');
      } catch (error) {
        toast.error('Erreur lors de la sauvegarde');
      }
    }, 800);
  };

  const deleteLogo = async (index) => {
    const token = localStorage.getItem('token');
    const newLogos = logos.filter((_, i) => i !== index);
    const logosForBackend = newLogos.map(logo => ({ 
      name: logo.name || '', 
      url: (logo.url || '').replace(API_URL, ''), 
      link: logo.link || '' 
    }));
    
    await fetch(`${API_URL}/api/my-site`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ site_settings: { client_logos: logosForBackend } })
    });
    
    setLogos(newLogos);
    toast.success('Logo supprimé');
  };

  // ========== MODELS ==========
  const handleModelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    
    if (!newModel.name) {
      toast.error('Veuillez entrer un nom pour le modèle');
      return;
    }
    
    setUploading(true);
    try {
      const imageUrl = await uploadImage(file);
      const token = localStorage.getItem('token');
      
      await fetch(`${API_URL}/api/photo-models`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          url: imageUrl,
          name: newModel.name,
          format: newModel.format,
          display_order: models.length
        })
      });
      
      setNewModel({ name: '', format: 'paysage' });
      loadAllContent();
      toast.success('Modèle ajouté');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setUploading(false);
      if (modelInputRef.current) modelInputRef.current.value = '';
    }
  };

  const deleteModel = async (modelId) => {
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/photo-models/${modelId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    setModels(models.filter(m => m.id !== modelId));
    toast.success('Modèle supprimé');
  };

  // ========== REALIZATIONS ==========
  const extractYouTubeId = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=|shorts\/)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const addRealization = async () => {
    const videoId = extractYouTubeId(newRealization.youtubeUrl);
    if (!videoId) {
      toast.error('URL YouTube invalide');
      return;
    }
    if (!newRealization.title) {
      toast.error('Veuillez entrer un titre');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/realizations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          videoId: videoId,
          youtubeUrl: newRealization.youtubeUrl,
          title: newRealization.title,
          category: newRealization.category,
          thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
          display_order: realizations.length
        })
      });
      
      setNewRealization({ youtubeUrl: '', title: '', category: 'videobooth-360' });
      loadAllContent();
      toast.success('Réalisation ajoutée');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const deleteRealization = async (realizationId) => {
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/realizations/${realizationId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    setRealizations(realizations.filter(r => r.id !== realizationId));
    toast.success('Réalisation supprimée');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-manager-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gestionnaire de Contenu</h1>
        <p className="text-sm text-gray-500">Gérez vos logos, modèles de photos et réalisations vidéo</p>
      </div>

      <Tabs defaultValue="logos" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="logos" className="flex items-center gap-2">
            <FiImage className="w-4 h-4" />
            Logos Clients ({logos.length})
          </TabsTrigger>
          <TabsTrigger value="models" className="flex items-center gap-2">
            <FiImage className="w-4 h-4" />
            Modèles Photos ({models.length})
          </TabsTrigger>
          <TabsTrigger value="realizations" className="flex items-center gap-2">
            <FiVideo className="w-4 h-4" />
            Réalisations ({realizations.length})
          </TabsTrigger>
        </TabsList>

        {/* LOGOS TAB */}
        <TabsContent value="logos">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FiImage className="w-5 h-5 text-amber-500" />
                Logos "Ils nous font confiance"
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload zone */}
              <div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors
                    ${uploading ? 'border-amber-500 bg-amber-50 cursor-wait' : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50'}`}
                >
                  {uploading ? (
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
                      <span className="mt-2 text-sm text-amber-600 font-medium">
                        Upload en cours... {uploadProgress.current}/{uploadProgress.total}
                      </span>
                      {uploadProgress.total > 0 && (
                        <div className="w-48 h-2 bg-amber-200 rounded-full mt-2 overflow-hidden">
                          <div 
                            className="h-full bg-amber-500 transition-all duration-300"
                            style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <FiUpload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">Cliquez ou glissez vos logos ici</span>
                      <span className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP (plusieurs fichiers possibles)</span>
                    </>
                  )}
                </label>
              </div>

              {/* Logos grid with URL field */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {logos.map((logo, idx) => (
                  <div key={idx} className="relative group bg-gray-50 rounded-lg p-3 border">
                    <div className="flex items-start gap-3">
                      {/* Logo image */}
                      <div className="w-20 h-20 flex-shrink-0 bg-white rounded-lg p-2 flex items-center justify-center border">
                        <img 
                          src={logo.url || logo} 
                          alt={logo.name || `Logo ${idx + 1}`} 
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                        />
                      </div>
                      
                      {/* URL Input */}
                      <div className="flex-1 min-w-0">
                        <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                          <FiExternalLink className="w-3 h-3" />
                          Lien URL (optionnel)
                        </Label>
                        <Input
                          value={logo.link || ''}
                          onChange={(e) => updateLogoLink(idx, e.target.value)}
                          placeholder="https://exemple.com"
                          className="text-sm h-8"
                        />
                        {logo.link && (
                          <a 
                            href={logo.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-amber-600 hover:underline mt-1 flex items-center gap-1"
                          >
                            <FiExternalLink className="w-3 h-3" />
                            Tester le lien
                          </a>
                        )}
                      </div>
                    </div>
                    
                    {/* Delete button */}
                    <button
                      onClick={() => deleteLogo(idx)}
                      className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      title="Supprimer ce logo"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {logos.length === 0 && (
                <p className="text-center text-gray-500 py-8">Aucun logo. Uploadez vos premiers logos clients.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MODELS TAB */}
        <TabsContent value="models">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FiImage className="w-5 h-5 text-amber-500" />
                Modèles de Photos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add model form */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <p className="text-sm font-medium text-gray-700">Ajouter un modèle</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <div>
                    <Label className="text-xs">Nom du modèle</Label>
                    <Input
                      value={newModel.name}
                      onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                      placeholder="Ex: Mariage Champêtre"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Format</Label>
                    <Select value={newModel.format} onValueChange={(v) => setNewModel({ ...newModel, format: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paysage">Paysage (10×15 cm)</SelectItem>
                        <SelectItem value="portrait">Portrait (15×10 cm)</SelectItem>
                        <SelectItem value="bandelette">Bandelette (5×15 cm)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <input
                      ref={modelInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleModelUpload}
                      className="hidden"
                      id="model-upload"
                    />
                    <Button 
                      onClick={() => modelInputRef.current?.click()}
                      disabled={!newModel.name || uploading}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                    >
                      <FiUpload className="w-4 h-4 mr-2" />
                      {uploading ? 'Upload...' : 'Uploader l\'image'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Models grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {models.map((model) => (
                  <div key={model.id} className="relative group">
                    <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden border">
                      <img 
                        src={model.url || model.image} 
                        alt={model.name} 
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                      />
                    </div>
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-900 truncate">{model.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{model.format}</p>
                    </div>
                    <button
                      onClick={() => deleteModel(model.id)}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {models.length === 0 && (
                <p className="text-center text-gray-500 py-8">Aucun modèle. Ajoutez vos premiers modèles de photos.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* REALIZATIONS TAB */}
        <TabsContent value="realizations">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FiVideo className="w-5 h-5 text-amber-500" />
                Réalisations Vidéo (YouTube)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add realization form */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <p className="text-sm font-medium text-gray-700">Ajouter une réalisation</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div className="md:col-span-2">
                    <Label className="text-xs">Lien YouTube</Label>
                    <Input
                      value={newRealization.youtubeUrl}
                      onChange={(e) => setNewRealization({ ...newRealization, youtubeUrl: e.target.value })}
                      placeholder="https://youtube.com/watch?v=... ou https://youtube.com/shorts/..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Titre</Label>
                    <Input
                      value={newRealization.title}
                      onChange={(e) => setNewRealization({ ...newRealization, title: e.target.value })}
                      placeholder="Ex: Mariage Julie & Marc"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Catégorie</Label>
                    <Select 
                      value={newRealization.category} 
                      onValueChange={(v) => setNewRealization({ ...newRealization, category: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="videobooth-360">VideoBooth 360</SelectItem>
                        <SelectItem value="livre-dor">Livre d'Or Vidéo</SelectItem>
                        <SelectItem value="photobooth">PhotoBooth</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button 
                  onClick={addRealization}
                  disabled={!newRealization.youtubeUrl || !newRealization.title}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <FiPlus className="w-4 h-4 mr-2" /> Ajouter la réalisation
                </Button>
              </div>

              {/* Realizations by category */}
              {['videobooth-360', 'livre-dor', 'photobooth'].map((category) => {
                const categoryRealizations = realizations.filter(r => r.category === category);
                if (categoryRealizations.length === 0) return null;
                
                const categoryLabel = {
                  'videobooth-360': 'VideoBooth 360',
                  'livre-dor': 'Livre d\'Or Vidéo',
                  'photobooth': 'PhotoBooth'
                }[category];
                
                return (
                  <div key={category} className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">{categoryLabel} ({categoryRealizations.length})</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {categoryRealizations.map((real) => (
                        <div key={real.id} className="relative group">
                          <div className={`bg-gray-900 rounded-lg overflow-hidden ${category === 'videobooth-360' ? 'aspect-[9/16]' : 'aspect-video'}`}>
                            <img 
                              src={real.thumbnail || `https://img.youtube.com/vi/${real.videoId}/maxresdefault.jpg`}
                              alt={real.title}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.src = `https://img.youtube.com/vi/${real.videoId}/hqdefault.jpg`; }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center opacity-80">
                                <FiPlay className="w-4 h-4 text-white ml-0.5" />
                              </div>
                            </div>
                          </div>
                          <p className="mt-2 text-sm text-gray-900 truncate">{real.title}</p>
                          <a 
                            href={real.youtubeUrl || `https://youtube.com/watch?v=${real.videoId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-amber-600 hover:underline flex items-center gap-1"
                          >
                            <FiExternalLink className="w-3 h-3" /> YouTube
                          </a>
                          <button
                            onClick={() => deleteRealization(real.id)}
                            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          >
                            <FiX className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              {realizations.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  Aucune réalisation. Ajoutez vos premières vidéos YouTube.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
